import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { PILLAR } from "contracts/enums/metrics";
import { For, Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { SeverityBadge, StatusBadge } from "../../ui/Badge";
import { healthVar } from "../../ui/health";
import { metricLabel, pillarOf } from "../website/issueModel";
import { createWebsiteStore } from "../website/store";
import { uniqueCurrentPagesForIssue } from "./issuePageResolution";
import { clientIssueTitle } from "./issueText";
import { ScanRouteSurface } from "./ScanRouteChrome";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";
import { signalsForPillar } from "./scanModel";

export function ScanAiReference(props: { websiteId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.AI_REFERENCE}>
      <ScanRouteSurface class="signal-ai-reference-route">
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Supporting diagnostics"
          title="AI Reference"
          subtitle="Owned-site AI discoverability and reference inputs, separate from the Content Fitness score."
        />

        <Show
          when={context.data()}
          keyed
          fallback={<div class="loading">Loading AI Reference...</div>}
        >
          {(data) => (
            <AiReferenceContent data={data} websiteId={props.websiteId} />
          )}
        </Show>
      </ScanRouteSurface>
    </ScanShell>
  );
}

function AiReferenceContent(props: {
  data: OverviewResponse;
  websiteId: string;
}) {
  const dimensions = () => signalsForPillar(props.data, PILLAR.AI_REFERENCE);
  const issues = () =>
    props.data.issues.filter(
      (issue) => pillarOf(issue.metricKey) === PILLAR.AI_REFERENCE,
    );
  return (
    <>
      <section class="scan-panel signal-diagnostic-context is-compact">
        <span class="scan-kicker">How this differs from Content Fitness</span>
        <h2>AI Reference is a supporting diagnostic</h2>
        <p>
          AI Agent Readiness is one of the five weighted Content Fitness
          dimensions and assesses whether page content can be accessed,
          understood and acted on by AI systems. AI Reference is a secondary
          owned-site diagnostic. It does not affect Content Fitness.
        </p>
        <details>
          <summary>What this diagnostic does not measure</summary>
          <p>
            External citations, representation, traffic, ranking and competitor
            outcomes remain unmeasured.
          </p>
        </details>
      </section>
      <section class="scan-panel">
        <div class="scan-section-head">
          <div>
            <span class="scan-kicker">Current scan data</span>
            <h2>Owned-site AI readiness inputs</h2>
          </div>
        </div>
        <Show
          when={dimensions().length > 0}
          fallback={
            <p class="scan-muted">
              No AI Reference readiness inputs were scored in this scan yet.
            </p>
          }
        >
          <div class="scan-mini-signal-list">
            <For each={dimensions()}>
              {(dimension) => (
                <article class="scan-mini-signal">
                  <div>
                    <strong>{dimension.label}</strong>
                    <p>{dimension.detail}</p>
                  </div>
                  <span
                    class="scan-score-pill mono"
                    style={{ color: healthVar(dimension.state) }}
                  >
                    {dimension.score}
                  </span>
                </article>
              )}
            </For>
          </div>
        </Show>
      </section>

      <section class="scan-panel">
        <div class="scan-section-head">
          <div>
            <span class="scan-kicker">Related findings</span>
            <h2>AI-related issues</h2>
          </div>
        </div>
        <Show
          when={issues().length > 0}
          fallback={
            <p class="scan-muted">No AI-related issues are linked yet.</p>
          }
        >
          <div class="scan-issue-list">
            <For each={issues()}>
              {(issue) => (
                <IssueRow
                  data={props.data}
                  issue={issue}
                  websiteId={props.websiteId}
                />
              )}
            </For>
          </div>
        </Show>
      </section>
    </>
  );
}

function IssueRow(props: {
  data: OverviewResponse;
  issue: Issue;
  websiteId: string;
}) {
  const affectedPageCount = () =>
    uniqueCurrentPagesForIssue(props.data, props.issue).length;
  return (
    <a
      class="scan-issue-row"
      href={`/w/${props.websiteId}/scan/issues/${props.issue.id}?from=issues`}
    >
      <SeverityBadge severity={props.issue.severity} />
      <span>
        <strong>{clientIssueTitle(props.issue)}</strong>
        <small>
          {metricLabel(props.issue.metricKey)} / {affectedPageCount()} page
          {affectedPageCount() === 1 ? "" : "s"}
        </small>
      </span>
      <StatusBadge status={props.issue.status} />
    </a>
  );
}
